import * as React from "react";
import { useTranslation } from "react-i18next";

import { DropZoneContent } from "@/components/_organisms/tiptap/tiptap-node/pdf-upload-node/drop-zone-content";
import { PdfCard } from "@/components/_organisms/tiptap/tiptap-node/pdf-upload-node/pdf-card";
import { UploadStatusRow } from "@/components/_organisms/tiptap/tiptap-node/pdf-upload-node/upload-status-row";
import { useFileUpload } from "@/components/_organisms/tiptap/tiptap-node/pdf-upload-node/use-file-upload";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";

export const PdfUploadNode: React.FC<NodeViewProps> = (props) => {
  const { t } = useTranslation("common");
  const { accept, limit, maxSize } = props.node.attrs;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const extension = props.extension;
  const [isUploading, setIsUploading] = React.useState(false);

  const { fileItem, uploadFiles, clearFileItem } = useFileUpload({
    accept,
    limit,
    maxSize,
    upload: extension.options.upload,
    onError: extension.options.onError,
    onSuccess: extension.options.onSuccess,
  });

  const handleUpload = async (files: File[]) => {
    setIsUploading(true);
    try {
      const url = await uploadFiles(files);
      if (url) {
        props.updateAttributes({
          file: {
            name: files[0]?.name,
            size: files[0]?.size,
            type: files[0]?.type,
          },
          url: url,
        });
        clearFileItem();
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = () => {
    props.updateAttributes({
      file: null,
      url: null,
    });
    clearFileItem();
    setIsUploading(false);
  };

  const handleClear = () => {
    clearFileItem();
    setIsUploading(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      handleUpload(files);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleUpload(files);
    }
  };

  if (props.node.attrs.file && props.node.attrs.url) {
    return (
      <NodeViewWrapper className="tiptap-pdf-upload" tabIndex={0} aria-label={t("editor.pdfFileCard")}>
        <PdfCard file={props.node.attrs.file} url={props.node.attrs.url} onDelete={handleDelete} />
      </NodeViewWrapper>
    );
  }

  if (!isUploading && !fileItem) {
    return (
      <NodeViewWrapper className="tiptap-pdf-upload" tabIndex={0} aria-label={t("editor.pdfUploadArea")}>
        <div
          className="relative rounded-md border-2 border-dashed border-slate-300 p-6 transition-colors hover:border-slate-400"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={limit > 1}
            onChange={handleFileChange}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={t("editor.uploadPdfFile")}
          />

          <DropZoneContent maxSize={maxSize} />
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper className="tiptap-pdf-upload" tabIndex={0} aria-label={t("editor.pdfUploadArea")}>
      <div className="relative rounded-md border-2 border-dashed border-slate-300 p-6 transition-colors hover:border-slate-400">
        {fileItem && (
          <UploadStatusRow
            fileName={fileItem.file.name}
            status={fileItem.status}
            progress={fileItem.progress}
            error={fileItem.error}
            onClear={handleClear}
          />
        )}
      </div>
    </NodeViewWrapper>
  );
};
